import React from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getViewerTimezone, getWeekStartInTimezone, getTodayYMDInTimezone, localDateFromYmd, formatLocalYMD, formatTimeInViewerTZ } from '../../utils/dateTimeUtils';
import { demoToNaiveCalendarEvent, outgoingSubstituteLabel, sessionToCalendarEvent } from '../../utils/calendarEvents';
import { availabilityToBusinessHours, earliestAvailabilityScrollTime } from '../../utils/teacherWorkingHours';
import { sessionKindLabel } from '../../utils/sessionKindDisplay';
import { buildBatchSessionPath } from '../../utils/teacherSessionNavigation';
import BatchStudentsHoverPopover, {
  useBatchStudentsHover,
} from '../BatchStudentsHoverPopover';

import './TeacherWeeklyTimetable.css';

const toYMD = (d) => formatLocalYMD(d);

function kindColors(kind) {
  if (kind === 'demo') return { bg: '#fff2df', border: '#ffd7a8', text: '#9a4b00' };
  if (kind === 'makeup') return { bg: '#efe7ff', border: '#d7c7ff', text: '#4b2aa7' };
  if (kind === 'cancelled') return { bg: '#f3f4f6', border: '#d1d5db', text: '#4b5563' };
  if (kind === 'holiday') return { bg: '#fff7ed', border: '#fdba74', text: '#c2410c' };
  if (kind === 'merged') return { bg: '#fde8f0', border: '#f5b4c8', text: '#9b1d4a' };
  if (kind === 'scheduled') return { bg: '#f0f4f8', border: '#c5d1e0', text: '#334e68' };
  if (kind === 'cover') return { bg: '#e6f7ed', border: '#9fd4b0', text: '#1b6b3a' };
  if (kind === 'substituted') return { bg: '#fff4e5', border: '#ffcc80', text: '#8a4b08' };
  if (kind === 'secondary') return { bg: '#e0f7f5', border: '#99d8d0', text: '#0f6b60' };
  return { bg: '#e8f1ff', border: '#cfe0ff', text: '#1656c2' }; // regular
}

export default function TeacherWeeklyTimetable({ teacherId, viewingTeacherName, calendarTimezone }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const calendarTz = getViewerTimezone(calendarTimezone || user?.timezone);
  const studentsHover = useBatchStudentsHover();
  const queriesEnabled = teacherId === undefined || Boolean(teacherId);
  const calendarRef = React.useRef(null);
  const [weekStart, setWeekStart] = React.useState(() => getWeekStartInTimezone(calendarTz));
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    const next = getWeekStartInTimezone(calendarTz);
    setWeekStart(next);
    const api = calendarRef.current?.getApi?.();
    if (api) api.gotoDate(localDateFromYmd(next));
  }, [calendarTz]);

  const from_date = weekStart;

  const homeWeekQuery = useQuery(
    ['teacherHomeCalendarWeek', from_date, teacherId, calendarTz],
    async () => {
      const params = { weekStart: from_date, viewer_timezone: calendarTz };
      if (teacherId) params.teacherId = teacherId;
      const res = await teacherModuleAPI.getHome(params);
      return res.data?.data;
    },
    { staleTime: 60_000, enabled: queriesEnabled, keepPreviousData: true }
  );

  const calendarFetching = homeWeekQuery.isFetching;

  const events = React.useMemo(() => {
    const weeklySessions = homeWeekQuery.data?.weeklySessions || [];
    const demos = homeWeekQuery.data?.weekDemos || [];
    const sessionEvents = weeklySessions.map(sessionToCalendarEvent).filter(Boolean);
    const demoEvents = demos.map((d) => {
      const ev = demoToNaiveCalendarEvent(d, calendarTz);
      if (!ev) return null;
      if (viewerTimezone !== calendarTz && d.scheduled_at_iso) {
        const yours = formatTimeInViewerTZ(d.scheduled_at_iso, viewerTimezone);
        if (yours) {
          ev.extendedProps = {
            ...ev.extendedProps,
            subtitle: [ev.extendedProps.subtitle, yours].filter(Boolean).join(' · '),
          };
        }
      }
      return ev;
    }).filter(Boolean);
    return [...sessionEvents, ...demoEvents];
  }, [homeWeekQuery.data, calendarTz, viewerTimezone]);

  const businessHours = React.useMemo(
    () => availabilityToBusinessHours(homeWeekQuery.data?.availability),
    [homeWeekQuery.data?.availability]
  );
  const hasWorkingHours = businessHours.length > 0;
  const scrollTime = earliestAvailabilityScrollTime(homeWeekQuery.data?.availability) || '06:00:00';

  return (
    <Card className={`teacher-weekly-timetable${hasWorkingHours ? ' has-working-hours' : ''}`}>
      <CardContent>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Weekly timetable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewingTeacherName
                ? `Schedule for ${viewingTeacherName}. Click a class to view details.`
                : 'Click a class to view details.'}
              {calendarTz ? ` Times shown in ${calendarTz}.` : ''}
              {viewerTimezone && calendarTz && viewerTimezone !== calendarTz
                ? ` Your clock: ${viewerTimezone}.`
                : ''}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
              {hasWorkingHours ? (
                <>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box className="teacher-weekly-timetable__legend-swatch teacher-weekly-timetable__legend-swatch--hours" />
                    <Typography variant="caption" color="text.secondary">
                      Working hours
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box className="teacher-weekly-timetable__legend-swatch teacher-weekly-timetable__legend-swatch--off" />
                    <Typography variant="caption" color="text.secondary">
                      Off
                    </Typography>
                  </Stack>
                </>
              ) : homeWeekQuery.data && !homeWeekQuery.data.missingTeacherProfile ? (
                <Typography variant="caption" color="text.secondary">
                  Working hours not set — empty slots do not mean the teacher is free.
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Box sx={{ position: 'relative' }}>
          {calendarFetching ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'background.paper',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">
                Loading week…
              </Typography>
            </Box>
          ) : null}
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            initialDate={localDateFromYmd(from_date)}
            firstDay={1}
            weekends={true}
            height="auto"
            expandRows={true}
            nowIndicator={true}
            allDaySlot={false}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="00:30:00"
            {...(hasWorkingHours ? { businessHours, scrollTime } : {})}
            customButtons={{
              teacherToday: {
                text: 'today',
                click: () => {
                  const ymd = getTodayYMDInTimezone(calendarTz);
                  calendarRef.current?.getApi()?.gotoDate(localDateFromYmd(ymd));
                },
              },
            }}
            headerToolbar={{
              left: 'prev,next teacherToday',
              center: 'title',
              right: '',
            }}
            titleFormat={{ year: 'numeric', month: 'short', day: 'numeric' }}
            dayHeaderContent={(arg) => ({ html: dayjs(arg.date).format('ddd D MMM') })}
            events={events}
            datesSet={(arg) => {
              const ymd = toYMD(arg.start);
              if (ymd && ymd !== weekStart) setWeekStart(ymd);
            }}
            eventClick={(info) => {
              const s = info.event.extendedProps?.session || null;
              const demoId = info.event.extendedProps?.demoId || null;
              if (demoId) {
                navigate(`/demos?view=${encodeURIComponent(demoId)}`);
                return;
              }
              const batchId = s?.batch_id || info.event.extendedProps?.batchId || null;
              const sessionId = s?.id || info.event.extendedProps?.sessionId || null;
              const sessionDate =
                (s?.session_date && String(s.session_date).slice(0, 10)) ||
                info.event.extendedProps?.sessionDate ||
                (info.event.start ? toYMD(info.event.start) : '');
              const startTime = s?.start_time || info.event.extendedProps?.startTime || null;
              const endTime = s?.end_time || info.event.extendedProps?.endTime || null;
              if (batchId) {
                const path = buildBatchSessionPath(batchId, sessionId, {
                  sessionDate,
                  startTime,
                  endTime,
                });
                if (path) navigate(path);
                return;
              }
              if (sessionId) {
                navigate(`/teacher/sessions/${encodeURIComponent(sessionId)}`);
                return;
              }
              if (s) setSelected(s);
            }}
            eventContent={(arg) => {
              const kind = arg.event.extendedProps?.kind || 'regular';
              const subtitle = arg.event.extendedProps?.subtitle || null;
              const session = arg.event.extendedProps?.session || null;
              const colors = kindColors(kind);
              const batchId = session?.batch_id || null;
              const batchName = session?.batch_name || arg.event.title || 'Batch';
              const canHoverStudents = kind !== 'demo' && !!batchId;

              return (
                <div
                  className="teacher-weekly-timetable__event"
                  style={{
                    background: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                  }}
                  onMouseEnter={
                    canHoverStudents
                      ? (e) =>
                          studentsHover.open(e.currentTarget, {
                            id: batchId,
                            name: batchName,
                            sessionId: session?.id || null,
                            sessionDate: session?.session_date || arg.event.extendedProps?.sessionDate || null,
                            startTime: session?.start_time || arg.event.extendedProps?.startTime || null,
                            endTime: session?.end_time || arg.event.extendedProps?.endTime || null,
                          })
                      : undefined
                  }
                  onMouseLeave={canHoverStudents ? studentsHover.onTriggerLeave : undefined}
                >
                  <div
                    className="teacher-weekly-timetable__event-title"
                    style={canHoverStudents ? { textDecoration: 'underline dotted' } : undefined}
                  >
                    {arg.event.title}
                  </div>
                  {subtitle ? (
                    <div className="teacher-weekly-timetable__event-subtitle">{subtitle}</div>
                  ) : null}
                </div>
              );
            }}
          />
        </Box>
      </CardContent>

      <BatchStudentsHoverPopover hover={studentsHover} />

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Class details</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={1.25}>
              <Typography variant="subtitle1" fontWeight={800}>
                {selected.batch_name || 'Batch'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.session_date}{' '}
                {selected.start_time ? String(selected.start_time).slice(0, 5) : ''} –{' '}
                {selected.end_time ? String(selected.end_time).slice(0, 5) : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {outgoingSubstituteLabel(selected)
                  ? `Substituted to ${String(selected.substitute_teacher_name || '').trim() || 'another teacher'}`
                  : `Kind: ${sessionKindLabel(selected) || selected.session_kind || 'regular'}`}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected && (selected.batch_id || selected.id) ? (
            <Button
              variant="contained"
              onClick={() => {
                if (selected.batch_id) {
                  const path = buildBatchSessionPath(selected.batch_id, selected.id, {
                    sessionDate: selected.session_date,
                    startTime: selected.start_time,
                    endTime: selected.end_time,
                  });
                  if (path) navigate(path);
                } else if (selected.id) {
                  navigate(`/teacher/sessions/${selected.id}`);
                }
              }}
            >
              Open details
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Card>
  );
}

